// SPDX-License-Identifier: Apache-2.0
import {
  AttributeTypeGroup,
  FilterOperator,
  filterOperators,
  isListFilterOperator,
  isTwoSidedFilterOperator,
  isValuelessFilterOperator,
  operatorsByAttributeTypeGroup
} from '../../models/filter';
import Geometry from 'ol/geom/Geometry';
import { GeoJSON } from 'ol/format';
import IGirafeContext from '../context/icontext';

/**
 * Base class for all filter conditions.
 */
export abstract class BaseFilterCondition<PropertyType> {
  public property: string;
  public propertyType?: PropertyType;
  public operator: FilterOperator;
  public value: string;
  public value2: string;
  public propertyTypeGroup?: AttributeTypeGroup;

  protected constructor(
    property: string,
    operator: FilterOperator,
    value?: string,
    value2?: string,
    propertyType?: PropertyType,
    propertyTypeGroup?: AttributeTypeGroup
  ) {
    this.property = property;
    this.operator = operator;
    this.value = value ?? '';
    this.value2 = value2 ?? '';
    this.propertyType = propertyType;
    this.propertyTypeGroup = propertyTypeGroup ?? 'string';

    if (isTwoSidedFilterOperator(this.operator) && !this.value2) {
      throw new Error('Operator needs two filter values !');
    }
  }

  /**
  Returns the supported operators for the given filter class.
   */
  public static get supportedOperators(): Array<{ operator: FilterOperator; displayName: string }> {
    return [...filterOperators];
  }

  /**
   * Returns the supported operators for the given attribute type. This function supports mapping for
   * the three basic attribute types: string, number and date. More diverse types must be mapped in the subclass.
   */
  public static supportedOperatorsByAttributeType(
    attributeType: string
  ): Array<{ operator: FilterOperator; displayName: string }> {
    return BaseFilterCondition.supportedOperators.filter((op) =>
      (operatorsByAttributeTypeGroup[attributeType as AttributeTypeGroup] ?? []).includes(op.operator)
    );
  }

  /**
   * Generates a simple text representation of the filter, for example, "attributeName [attributeType] = value"
   * Needs the context to translate the operator and other terms.
   * If instead of the attribute name and type, an alias or translated name should be displayed,
   * provide the string via the optional propertyDisplayName parameter
   */
  public toTextRepresentation(context: IGirafeContext, propertyDisplayName?: string): string {
    const i18n = (key: string) => context.i18nManager.getTranslation(key);

    if (!propertyDisplayName) {
      propertyDisplayName = `${this.property} [${i18n((this.propertyType as string) ?? '')}]`;
    }
    const operatorDisplayName =
      filterOperators.find((op) => op.operator === this.operator)?.displayName ?? this.operator;
    const attributeAndOperator = `${propertyDisplayName} ${i18n(operatorDisplayName)}`;
    const quotes = this.propertyTypeGroup === 'date' || this.propertyTypeGroup === 'string' ? '"' : '';

    if (this.propertyTypeGroup === 'geometry') {
      return `${attributeAndOperator} ${i18n(BaseFilterCondition.geometryToTextRepresentation(this.value))}`;
    }
    if (isValuelessFilterOperator(this.operator)) {
      return attributeAndOperator;
    }
    if (isTwoSidedFilterOperator(this.operator)) {
      return `${attributeAndOperator} ${quotes}${this.value}${quotes} ${i18n('AND')} ${quotes}${this.value2}${quotes}`;
    }
    if (isListFilterOperator(this.operator)) {
      const items = (JSON.parse(this.value).items ?? []) as string[];
      const itemsAsCommaList = items.map((val) => `${quotes}${val}${quotes}`).join(', ');
      return `${attributeAndOperator} ${itemsAsCommaList}`;
    }
    return `${attributeAndOperator} ${quotes}${this.value}${quotes}`;
  }

  private static geometryToTextRepresentation(geoJson: string): string {
    const feature = JSON.parse(geoJson);
    if (feature?.properties?.tool) {
      // If the drawing tool is specified (circle, rectangle, etc.), return it as the geometry type
      return feature.properties.tool;
    } else {
      // Fallback to default geometry type
      const format = new GeoJSON();
      const geometry = format.readGeometry(geoJson);
      return (geometry ?? new Geometry()).getType();
    }
  }
}
